import React, { useEffect, useState } from "react";
import moment from "moment";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Row,
    Table,
    Button,
} from "reactstrap";
import Select from "react-select";
import Header from "components/Headers/Header";
import { getFlaggingReport, getUser } from "utils/endpoint";
import { Pagination as Paginationes } from "antd";
import UseFullPageLoader from "utils/UseFullPageLoader";

const FlaggingReport = () => {
    const [loader, showLoader, hideLoader] = UseFullPageLoader();
    const [reportData, setReportData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [userOptions, setUserOptions] = useState([]);

    // Filters
    const [medium, setMedium] = useState(null);
    const [series, setSeries] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [flagger, setFlagger] = useState(null);
    const [flaggee, setFlaggee] = useState(null);
    const [status, setStatus] = useState(null);
    const [sortBy, setSortBy] = useState("flag_date");
    const [sortOrder, setSortOrder] = useState("DESC");

    const mediumOptions = [
        { value: "manga", label: "Manga" },
        { value: "light-novel", label: "Light Novel" },
        { value: "webtoon", label: "Webtoon" },
    ];

    const statusOptions = [
        { value: "Pending", label: "Pending" },
        { value: "Read", label: "Read" },
    ];

    const fetchUsers = async () => {
        try {
            const response = await getUser();
            if (response && response.isSuccess) {
                const options = response.data.map(u => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name}`
                }));
                setUserOptions(options);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const fetchReport = async () => {
        showLoader();
        try {
            const params = {
                page,
                limit,
                medium: medium?.value,
                series,
                fromDate,
                toDate,
                flagger: flagger?.value,
                flaggee: flaggee?.value,
                status: status?.value,
                sortBy,
                sortOrder,
            };
            const response = await getFlaggingReport(params);
            if (response && response.isSuccess) {
                setReportData(response.data.data);
                setTotal(response.data.total);
            }
        } catch (error) {
            console.error("Error fetching flagging report:", error);
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchReport();
    }, [page, limit, sortBy, sortOrder]);

    const handleSearch = () => {
        setPage(1);
        fetchReport();
    };

    const clearFilters = () => {
        setMedium(null);
        setSeries("");
        setFromDate("");
        setToDate("");
        setFlagger(null);
        setFlaggee(null);
        setStatus(null);
        setPage(1);
        // fetchReport will be triggered by useEffect if any of the dependencies change, 
        // but here we manually call it to be sure.
        setTimeout(fetchReport, 100);
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
        } else {
            setSortBy(column);
            setSortOrder("ASC");
        }
    };

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <h3 className="mb-0">Flagging Report</h3>
                                <Row className="mt-3">
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">Medium</label>
                                            <Select
                                                isClearable
                                                value={medium}
                                                options={mediumOptions}
                                                onChange={(val) => setMedium(val)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">Series</label>
                                            <Input
                                                placeholder="Search series..."
                                                value={series}
                                                onChange={(e) => setSeries(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">Flagger</label>
                                            <Select
                                                isClearable
                                                value={flagger}
                                                options={userOptions}
                                                onChange={(val) => setFlagger(val)}
                                                placeholder="Select flagger..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">Flaggee</label>
                                            <Select
                                                isClearable
                                                value={flaggee}
                                                options={userOptions}
                                                onChange={(val) => setFlaggee(val)}
                                                placeholder="Select flaggee..."
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">Status</label>
                                            <Select
                                                isClearable
                                                value={status}
                                                options={statusOptions}
                                                onChange={(val) => setStatus(val)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">From Date</label>
                                            <Input
                                                type="date"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="2">
                                        <FormGroup>
                                            <label className="form-control-label">To Date</label>
                                            <Input
                                                type="date"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg="4" className="d-flex align-items-end mb-3">
                                        <Button color="primary" onClick={handleSearch}>
                                            Search
                                        </Button>
                                        <Button color="secondary" onClick={clearFilters}>
                                            Clear
                                        </Button>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Table className="align-items-center table-flush" responsive>
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ cursor: "pointer" }} onClick={() => handleSort("medium")}>
                                                Medium {sortBy === "medium" && (sortOrder === "ASC" ? "↑" : "↓")}
                                            </th>
                                            <th style={{ cursor: "pointer" }} onClick={() => handleSort("series")}>
                                                Series {sortBy === "series" && (sortOrder === "ASC" ? "↑" : "↓")}
                                            </th>
                                            <th style={{ cursor: "pointer" }} onClick={() => handleSort("flag_date")}>
                                                Flag Date {sortBy === "flag_date" && (sortOrder === "ASC" ? "↑" : "↓")}
                                            </th>
                                            <th style={{ cursor: "pointer" }} onClick={() => handleSort("flagger_name")}>
                                                Flagger {sortBy === "flagger_name" && (sortOrder === "ASC" ? "↑" : "↓")}
                                            </th>
                                            <th style={{ cursor: "pointer" }} onClick={() => handleSort("flaggee_name")}>
                                                Flaggee {sortBy === "flaggee_name" && (sortOrder === "ASC" ? "↑" : "↓")}
                                            </th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.length > 0 ? (
                                            reportData.map((row, index) => (
                                                <tr key={index}>
                                                    <td className="text-capitalize">{row.medium}</td>
                                                    <td>{row.series}</td>
                                                    <td>{moment(row.flag_date).format("YYYY-MM-DD HH:mm")}</td>
                                                    <td>{row.flagger_name}</td>
                                                    <td>{row.flaggee_name}</td>
                                                    <td>
                                                        <span className={`badge badge-${row.is_read ? "success" : "warning"}`}>
                                                            {row.is_read ? "Read" : "Pending"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center">
                                                    No data found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </CardBody>
                            <div className="card-footer py-4">
                                <Paginationes
                                    current={page}
                                    total={total}
                                    pageSize={limit}
                                    onChange={(p, s) => {
                                        setPage(p);
                                        setLimit(s);
                                    }}
                                    showSizeChanger={true}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {loader}
        </>
    );
};

export default FlaggingReport;
